<template>
  <div
    :id="uuid"
    class="p-6 md:p-8 bg-white rounded-xl"
    :style="{
      'scroll-margin-top': pageEditorStore.scrollMarginTop,
    }"
  >
    <PaymentCloudPayments
      ref="cloudPaymentsComponent"
      @done="onPaymentDone"
      @close="onPaymentClose"
    />
    <h2
      v-if="formData.title"
      class="text-2xl/[1] md:text-3xl/[1.20] font-bold mb-4 md:mb-6"
      :style="{
        textAlign: formData.titleAlign,
        fontWeight: formData.titleWeight,
        fontStyle: formData.titleStyle,
        color: formData.titleColor,
        fontSize: formData.titleSizePx,
        ...useEditorFontFamily(formData.titleFontFamily),
      }"
    >
      {{ formData.title }}
    </h2>
    <!-- <SubscriptionsSuccessBlock
      v-if="authStore.profile && isShowSuccessCard"
      :profile="authStore.profile"
    /> -->
    <div class="space-y-3 text-gray">
      <template v-if="authStore.isProActive">
        <SubscriptionsProHeader
          :subscription="subStore.subscription"
          :is-pro-active="authStore.isProActive"
        />
        <div v-if="isSubscriptionLoading" class="py-10 flex justify-center">
          <BaseSpinner />
        </div>
        <template v-else-if="subStore.subscription">
          <SubscriptionsManageBlock
            :subscription="subStore.subscription"
            @unsubscribe="onUnsubscribe"
          />
          <!-- <div v-if="isTicketsLoading" class="py-10 flex justify-center">
            <BaseSpinner />
          </div>
          <div
            v-else-if="
              subStore.tickets.length &&
              Object.values(eventsStore.eventsMap).length
            "
            class="flex flex-col gap-6 bg-white rounded-xl p-4 sm:p-6"
          >
            <div class="text-2xl text-gray-900 font-bold">
              {{ t('subscriptions.tickets_title') }}
            </div>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-12">
              <template v-for="ticket in subStore.tickets" :key="ticket.id">
                <SubscriptionsTicketCard
                  :ticket="ticket"
                  :event="eventsStore.eventsMap[ticket.event_id]"
                  @activated="onActivated"
                />
              </template>
            </div>
          </div> -->
        </template>
      </template>
      <div v-else class="flex flex-col gap-4">
        <div v-if="formData.subscriptionsTitle" class="text-xl font-bold">
          {{ formData.subscriptionsTitle }}
        </div>
        <div v-if="isPlansLoading" class="py-10 flex justify-center">
          <BaseSpinner />
        </div>
        <template v-else-if="plans.length">
          <div
            class="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-2 xl:grid-cols-3 gap-3"
          >
            <template v-for="(plan, index) in plans" :key="plan.id">
              <SubscriptionsPlanCard
                :selected="plan === selectedPlan"
                :title="getFieldLocale(plan.name, i18n.locale.value)"
                :description="
                  getFieldLocale(plan.description, i18n.locale.value)
                "
                :price="plan.prices[0]"
                :period="plan.period"
                :is-hidden="!plan.is_onsite"
                @click="selectedPlanIndex = index"
              />
            </template>
          </div>
          <div
            v-if="disclaimerHtml"
            class="bg-gray-100/50 p-4 rounded-xl marked"
            v-html="disclaimerHtml"
          ></div>
          <div class="flex flex-col items-center justify-center">
            <BaseButton
              class="w-full md:w-auto"
              :loading="isPaymentDataLoading"
              :disabled="isPaymentDataLoading"
              @click="submit"
            >
              {{ t('common.subscribe') }}
            </BaseButton>
          </div>
        </template>
        <div v-else>
          {{ $t('subscriptions.no_plans') }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { Browser } from '@capacitor/browser'
import { useAuth } from '~/stores/auth'
import { useSubscriptions } from '~/stores/subscriptions'
import { useNotifications } from '~/stores/notifications'
import { useOrders } from '~/stores/orders'
import { usePageEditor } from '~/stores/page-editor'
import type { Plan, PlanPrice } from '~/models/subscriptions'
import PaymentCloudPayments from '~/components/payment/PaymentCloudPayments.vue'
// import { SubscriptionTicketStatus } from '~/models/subscriptions'
// import { useEvents } from '~/stores/events'

interface PropsInterface {
  uuid: string
  formData: any
  isInIframe?: boolean
}

const props = withDefaults(defineProps<PropsInterface>(), {
  isInIframe: false,
})

const { getMarkedTextWithBlankLinks } = useMarkedText()
const pageEditorStore = usePageEditor()
const authStore = useAuth()
const subStore = useSubscriptions()
const {
  fetchPlansList,
  fetchActiveSubscription,
  buySubscription,
  resetSubscription,
  resetPlans,
  // fetchSubscriptionTickets,
} = subStore
// const eventsStore = useEvents()
// const router = useRouter()
// const isTicketsLoading = ref(false)
// const isShowSuccessCard = computed(() => {
//   return router.currentRoute.value.fullPath.includes('success')
// })
const { t } = useI18n()
const i18n = useI18n()
const { $dayjs } = useNuxtApp()
const { pushNotification } = useNotifications()

const { getHumanizedDate } = useDate()
const { resetPaymentsList } = useOrders()

const selectedPlanIndex = ref(0)
const isSubscriptionLoading = ref(false)

const isPlansLoading = ref(false)
const isPaymentDataLoading = ref(false)
const isNavigated = ref(false)

const cloudPaymentsComponent = ref<null | InstanceType<
  typeof PaymentCloudPayments
>>(null)

const plans = computed<Plan[]>(
  () =>
    props.formData?.plans
      ?.map((planId: number) => subStore.plansMap[planId])
      ?.filter(Boolean) || []
)

const selectedPlan = computed<Plan | undefined>(
  () => plans.value[selectedPlanIndex.value]
)
const selectedPlanPrice = computed<PlanPrice | undefined>(
  () => selectedPlan.value?.prices[0]
)

const fetchPlans = async () => {
  isPlansLoading.value = true
  await fetchPlansList(authStore.isAdmin)
  isPlansLoading.value = false
}

const fetchSubscription = async () => {
  isSubscriptionLoading.value = true
  try {
    await fetchActiveSubscription()
  } catch (error: any) {
    if (error.status === 404) {
      resetSubscription()
      return
    }
    throw error
  } finally {
    isSubscriptionLoading.value = false
  }
}

const planDescriptionStartDate = computed(() => {
  const startDate = $dayjs().tz()
  return getHumanizedDate(startDate)
})

const planDescriptionEndDate = computed(() => {
  const period = selectedPlan.value?.period
  if (!period) {
    return ''
  }

  const today = $dayjs().tz()
  const endDate = today.add(period, 'month')

  return getHumanizedDate(endDate)
})

const disclaimerHtml = computed(() => {
  return getMarkedTextWithBlankLinks(props.formData.disclaimerText)
    .replace(/{{startDate}}/g, planDescriptionStartDate.value)
    .replace(/{{endDate}}/g, planDescriptionEndDate.value)
})

const submit = async () => {
  if (!authStore.auth) {
    authStore.openLoginModal()
    return
  }

  if (
    isPaymentDataLoading.value ||
    !selectedPlan.value ||
    !selectedPlanPrice.value
  ) {
    return
  }

  const isApp = useDetect().isApp

  isPaymentDataLoading.value = true
  try {
    const paymentData = await buySubscription(
      selectedPlan.value.id,
      selectedPlanPrice.value.currency
    )

    if (paymentData.redirect_url) {
      if (isApp) {
        await Browser.open({ url: paymentData.redirect_url })
      } else {
        window.location.href = paymentData.redirect_url
        isNavigated.value = true
      }
    } else if (paymentData.sub_id) {
      isNavigated.value = true
      await processCloudPayments(paymentData.sub_id)
    }

    resetPaymentsList()
  } catch (error) {
    pushNotification({
      title: t('notifications.tickets.create_url_error'),
      theme: 'destructive',
    })
    throw error
  } finally {
    !isNavigated.value && (isPaymentDataLoading.value = false)
  }
}

const processCloudPayments = async (invoiceId: string) => {
  if (!selectedPlan.value || !selectedPlanPrice.value) {
    return
  }

  const productName = getFieldLocale(selectedPlan.value.name, i18n.locale.value)

  await cloudPaymentsComponent.value?.processCloudPayments(
    {
      amount: selectedPlanPrice.value.amount,
      currency: selectedPlanPrice.value.currency,
      invoiceId,
      description: productName,
      receiptLabel: productName,
    },
    undefined,
    {
      interval: authStore.profile?.id === 4944 ? 'Day' : 'Month', // TODO: remove 4944
      period: selectedPlan.value.period,
    }
  )
}

const onPaymentDone = async () => {
  await authStore.refreshProfile()
  onPaymentClose()
}

const onPaymentClose = () => {
  isNavigated.value = false
  isPaymentDataLoading.value = false
}

const onUnsubscribe = () => {
  resetSubscription()
  resetPaymentsList()
  fetchSubscription()
}

// const onActivated = (ticketId: number) => {
//   const ticket = subStore.tickets.find(({ id }) => id === ticketId)

//   if (ticket) {
//     ticket.status = SubscriptionTicketStatus.Activated
//     eventsStore.eventsMap[ticket.event_id].is_has_ticket = true
//   }
// }

const load = async () => {
  if (authStore.isProActive) {
    await fetchSubscription()

    // if (authStore.isProActive) {
    //   try {
    //     isTicketsLoading.value = true
    //     await fetchSubscriptionTickets()

    //     const eventIds = subStore.tickets.map((ticket) => ticket.event_id)
    //     await eventsStore.fetchEventsByIds(eventIds)
    //   } catch (error) {
    //     useLogError(error)
    //   } finally {
    //     isTicketsLoading.value = false
    //   }
    // }
  } else {
    await fetchPlans()
  }
}

onServerPrefetch(async () => {
  await load()
})

onMounted(() => {
  load()
})

watch(
  () => authStore.profile,
  () => {
    resetPlans()
    resetSubscription()
    selectedPlanIndex.value = 0
    load()
  }
)
</script>
